import React, { FC, useMemo, useState } from "react";
import Modal from "@/components/Modal/index";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal/PermissionDeniedModal";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Button from "@/components/Button";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import {
  useCountrySetting,
  useMinAgeAllowed,
  useClientIdSetting,
  useBackofficeParticipantPhoneNumberSetting,
  useBackofficeParticipantDateOfBirthSetting,
} from "@/hooks/useSetting";
import Select from "@/components/Select";
import Typography from "@mui/material/Typography";
import { getUSStatesForSelect } from "@/utils/us-states.enum";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import PhoneNumberInput from "@/components/PhoneNumberInput";
import Datepicker from "@/components/Datepicker";
import dayjs, { Dayjs } from "dayjs";
import registerParticipantValidationSchema, {
  ParticipantRegisterFormData,
} from "./validation.schema";
import { useCreateParticipant } from "@/hooks/useParticipants";
import { useNavigate } from "react-router-dom";
import { getGeocodeAlertKey } from "@/utils/geocodeAlert";

type ParticipantRegisterModalProps = {
  open: boolean;
  onClose: () => void;
};

const ParticipantRegisterModal: FC<ParticipantRegisterModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation("participant");
  const { t: tCommon } = useTranslation("common");
  const { userInfo, userHasAccessToCode } = useUserStore();
  const { showSnackBar, showSimpleAlert } = useFeedBack();
  const theme = useAppTheme();
  const [showPermissionDeniedModal, setShowPermissionDeniedModal] =
    useState(false);

  const createAuditLog = useCreateAuditlog();
  const { isPending, mutateAsync } = useCreateParticipant();

  const countrySetting = useCountrySetting();
  const minAgeAllowed = useMinAgeAllowed();
  const clientIdSetting = useClientIdSetting();
  const backofficeParticipantPhoneNumberSetting =
    useBackofficeParticipantPhoneNumberSetting();
  const backofficeParticipantDateOfBirthSetting =
    useBackofficeParticipantDateOfBirthSetting();
  const navigate = useNavigate();

  const country = useMemo(() => countrySetting, [countrySetting]);
  // Use backofficeParticipantPhoneNumber setting
  const phoneMandatory = useMemo(
    () => backofficeParticipantPhoneNumberSetting === "mandatory",
    [backofficeParticipantPhoneNumberSetting],
  );
  // Use backofficeParticipantDateOfBirth setting
  const dateOfBirthMandatory = useMemo(
    () => backofficeParticipantDateOfBirthSetting === "mandatory",
    [backofficeParticipantDateOfBirthSetting],
  );
  const dateOfBirthVisible = useMemo(
    () => backofficeParticipantDateOfBirthSetting !== "hidden",
    [backofficeParticipantDateOfBirthSetting],
  );
  const cinMandatory = useMemo(
    () => clientIdSetting === "mandatory",
    [clientIdSetting],
  );

  const schema = useMemo(() => {
    return registerParticipantValidationSchema(
      tCommon,
      country,
      dateOfBirthMandatory,
      phoneMandatory as boolean,
      minAgeAllowed,
      cinMandatory,
    );
  }, [
    tCommon,
    country,
    dateOfBirthMandatory,
    phoneMandatory,
    minAgeAllowed,
    cinMandatory,
  ]);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ParticipantRegisterFormData>({
    resolver: yupResolver(schema),
    mode: "all",
  });

  const onSubmit = async (data: ParticipantRegisterFormData) => {
    const payload = {
      firstName: data.firstName as string,
      lastName: data.lastName as string,
      email: data.email as string,
      countryPhoneCode: country === "US" ? "1" : "44",
      phoneNumber: data.phoneNumber as string,
      createdBy: userInfo?._id as string,
      dateOfBirth: data.dateOfBirth
        ? dayjs(data.dateOfBirth as string).toDate()
        : undefined,
      cin: (data.cin || "") as string,
      address: {
        house: (data as any).house || (data as any).primaryAddressLine1 || "",
        locality: (data as any).locality || (data as any).primaryCity || "",
        postalCode:
          (data as any).postalCode || (data as any).primaryZipCode || "",
        addressLine1: (data as any).primaryAddressLine1 || "",
        addressLine2: (data as any).primaryAddressLine2 || "",
        city: (data as any).primaryCity || "",
        state: (data as any).primaryState || "",
        zipCode: (data as any).primaryZipCode || "",
        county: (data as any).primaryCounty || "",
        secondaryAddressLine1: (data as any).secondaryAddressLine1 || "",
        secondaryAddressLine2: (data as any).secondaryAddressLine2 || "",
        secondaryCity: (data as any).secondaryCity || "",
        secondaryState: (data as any).secondaryState || "",
        secondaryZipCode: (data as any).secondaryZipCode || "",
        secondaryCounty: (data as any).secondaryCounty || "",
      },
    };

    try {
      const data = await mutateAsync(payload);
      onClose();
      const geocodeAlertKey = getGeocodeAlertKey(data);
      if (geocodeAlertKey) {
        showSimpleAlert(t(geocodeAlertKey));
      } else {
        showSnackBar("The client has been invited", "success");
      }

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.PARTICIPANTS,
        option: OPTIONS.PARTICIPANT_ADD,
        actionCode: ACTION_CODE.WEB_PART_ADD_INVITE,
        action: "When adding a new participant (inviting)",
        detail: `Invited new participant ${data.firstName} ${data.lastName}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        targetId: data._id,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
      navigate("/participants/" + data._id);
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      let errorMessage: string;
      if (Array.isArray(err.message)) {
        const firstMessage = err.message[0];
        errorMessage =
          typeof firstMessage === "string"
            ? firstMessage
            : (firstMessage as any)?.errorMessage || "An error occurred";
      } else if (typeof err.message === "object" && err.message !== null) {
        errorMessage =
          (err.message as any)?.errorMessage ||
          err.error ||
          "An error occurred";
      } else {
        errorMessage = err.message || err.error || "An error occurred";
      }

      // Handle specific email conflict errors (AC02-a, AC02-b)
      if (
        errorMessage.includes("already exists") ||
        errorMessage.includes("already active")
      ) {
        if (errorMessage.includes("another account")) {
          showSimpleAlert(
            "A client with this email is already active on another account.",
          );
        } else {
          showSimpleAlert("A client with this email already exists.");
        }
      } else {
        showSimpleAlert(errorMessage);
      }
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        isPending={false}
        title="Participant"
        theme={theme}
        size={{
          width: "100%",
          minHeight: 600,
          maxHeight: 900,
          maxWidth: 860,
        }}
        content={
          <Box sx={{ width: "100%", minHeight: 380, padding: "20px 0" }}>
            <Grid container rowSpacing={1.6} columnSpacing={0.8}>
              <Grid size={12}>
                <Typography
                  variant="titleMedium"
                  sx={{ color: theme.palette.primary.main, mb: 1 }}
                >
                  General information
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="firstName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="firstName"
                      label={t("label.first-name")}
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.firstName}
                      helperText={
                        errors.firstName ? errors.firstName.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="lastName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="lastName"
                      label={t("label.last-name")}
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.lastName}
                      helperText={
                        errors.lastName ? errors.lastName.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="email"
                      label={t("label.email")}
                      required
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.email}
                      helperText={errors.email ? errors.email.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="phoneNumber"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <PhoneNumberInput
                      {...field}
                      defaultCode={country}
                      value={field.value}
                      label={t("label.phone-number")}
                      variant="outlined"
                      className="flex flex-1 w-full"
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.phoneNumber}
                      helperText={
                        errors.phoneNumber ? errors.phoneNumber.message : null
                      }
                      required={phoneMandatory}
                    />
                  )}
                />
              </Grid>
              {dateOfBirthVisible ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="dateOfBirth"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <Datepicker
                          {...field}
                          label={t("label.date-birth", {
                            format:
                              country === "US"
                                ? "(mm/dd/yyyy)"
                                : "(dd/mm/yyyy)",
                          })}
                          onChange={(value) => {
                            field.onChange(
                              value !== undefined ? value?.toDate() : undefined,
                            );
                          }}
                          value={
                            field.value
                              ? dayjs(
                                  field.value as string | number | Date | Dayjs,
                                )
                              : null
                          }
                          error={!!errors.dateOfBirth}
                          helperText={errors.dateOfBirth?.message || ""}
                          required={dateOfBirthMandatory}
                        />
                      </div>
                    )}
                  />
                </Grid>
              ) : null}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="cin"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="cin"
                      label={t("label.cin")}
                      required={cinMandatory}
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.cin}
                      helperText={errors.cin ? errors.cin.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              {/* Primary Address Section */}
              <Grid size={12}>
                <Typography
                  variant="titleMedium"
                  sx={{ color: theme.palette.primary.main, mb: 1 }}
                >
                  Primary address
                </Typography>
              </Grid>
              <Grid size={12}>
                <Controller
                  name="primaryAddressLine1"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="primaryAddressLine1"
                      label="Address line 1"
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).primaryAddressLine1}
                      helperText={
                        (errors as any).primaryAddressLine1?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  name="primaryAddressLine2"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="primaryAddressLine2"
                      label="Address line 2"
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).primaryAddressLine2}
                      helperText={
                        (errors as any).primaryAddressLine2?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="primaryCity"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="primaryCity"
                      label={country === "US" ? "City" : "Town / City"}
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).primaryCity}
                      helperText={(errors as any).primaryCity?.message || null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              {country === "US" ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="primaryState"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        id="primaryState"
                        label="State"
                        data={getUSStatesForSelect()}
                        value={
                          typeof field.value === "string" ? field.value : ""
                        }
                        handleChange={(value) => field.onChange(value)}
                        error={!!(errors as any).primaryState}
                        errorMessage={
                          typeof (errors as any).primaryState?.message ===
                          "string"
                            ? (errors as any).primaryState?.message
                            : (errors as any).primaryState?.message
                                ?.errorMessage
                        }
                      />
                    )}
                  />
                </Grid>
              ) : country === "UK" ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="primaryCounty"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="primaryCounty"
                        label="County"
                        variant="outlined"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        error={!!(errors as any).primaryCounty}
                        helperText={
                          (errors as any).primaryCounty?.message || null
                        }
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              ) : null}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="primaryZipCode"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="primaryZipCode"
                      label={country === "US" ? "ZIP" : "Postal code"}
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).primaryZipCode}
                      helperText={
                        (errors as any).primaryZipCode?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Secondary Address Section */}
              <Grid size={12}>
                <Typography
                  variant="titleMedium"
                  sx={{ color: theme.palette.primary.main, mb: 1, mt: 2 }}
                >
                  Secondary address
                </Typography>
              </Grid>
              <Grid size={12}>
                <Controller
                  name="secondaryAddressLine1"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="secondaryAddressLine1"
                      label="Address line 1"
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).secondaryAddressLine1}
                      helperText={
                        (errors as any).secondaryAddressLine1?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  name="secondaryAddressLine2"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="secondaryAddressLine2"
                      label="Address line 2"
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).secondaryAddressLine2}
                      helperText={
                        (errors as any).secondaryAddressLine2?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="secondaryCity"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="secondaryCity"
                      label={country === "US" ? "City" : "Town / City"}
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).secondaryCity}
                      helperText={
                        (errors as any).secondaryCity?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              {country === "US" ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="secondaryState"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        id="secondaryState"
                        label="State"
                        data={getUSStatesForSelect()}
                        value={
                          typeof field.value === "string" ? field.value : ""
                        }
                        handleChange={(value) => field.onChange(value)}
                        error={!!(errors as any).secondaryState}
                        errorMessage={
                          typeof (errors as any).secondaryState?.message ===
                          "string"
                            ? (errors as any).secondaryState?.message
                            : (errors as any).secondaryState?.message
                                ?.errorMessage
                        }
                      />
                    )}
                  />
                </Grid>
              ) : country === "UK" ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="secondaryCounty"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="secondaryCounty"
                        label="County"
                        variant="outlined"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        error={!!(errors as any).secondaryCounty}
                        helperText={
                          (errors as any).secondaryCounty?.message || null
                        }
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              ) : null}
              <Grid size={{ xs: 12, sm: 6 }} sx={{ mb: 3 }}>
                <Controller
                  name="secondaryZipCode"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="secondaryZipCode"
                      label={country === "US" ? "ZIP" : "Postal code"}
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).secondaryZipCode}
                      helperText={
                        (errors as any).secondaryZipCode?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        }
        actions={
          <Box
            sx={{
              display: "flex",
              gap: "10px",
            }}
          >
            <Button
              variantType="cancel"
              onClick={onClose}
              disabled={isPending}
              label={t("button.cancel")}
              sx={{ textTransform: "uppercase" }}
            />
            <Button
              variantType="save"
              onClick={async () => {
                if (!userHasAccessToCode("PARTICIPANT_ADD")) {
                  setShowPermissionDeniedModal(true);
                  return;
                }
                handleSubmit(onSubmit)();
              }}
              disabled={isPending}
              isLoading={isPending}
              label="Invite"
              sx={{ textTransform: "uppercase" }}
            />
          </Box>
        }
      />
      <PermissionDeniedModal
        open={showPermissionDeniedModal}
        onClose={() => setShowPermissionDeniedModal(false)}
      />
    </>
  );
};

export default ParticipantRegisterModal;
